import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_SCHOLARSHIPS, GET_SCHOLARSHIP, MY_SCHOLARSHIPS, MY_SCHOLARSHIP_APPLICATIONS,
  SCHOLARSHIP_APPLICANTS, CREATE_SCHOLARSHIP, UPDATE_SCHOLARSHIP, DELETE_SCHOLARSHIP,
  APPLY_FOR_SCHOLARSHIP, WITHDRAW_SCHOLARSHIP_APPLICATION, REVIEW_SCHOLARSHIP_APPLICATION,
} from '../graphql';
import {
  Award, Search, Filter, Plus, ChevronDown, ChevronUp, Check, X, Clock,
  DollarSign, Users, Calendar, BookOpen, Briefcase, ExternalLink, Trash2, Edit3,
  GraduationCap, TrendingUp, AlertCircle, CheckCircle, XCircle,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN').format(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const isExpired = (d) => d && new Date(d) < new Date();

const STATUS_COLORS = {
  OPEN:   'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  CLOSED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  DRAFT:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
};
const APP_STATUS_COLORS = {
  PENDING:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  ACCEPTED: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  WAITLIST: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const EligBadge = ({ met, label }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${met ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
    {met ? <Check size={11} /> : <X size={11} />} {label}
  </span>
);

// ── ScholarshipCard ───────────────────────────────────────────────────────────
function ScholarshipCard({ s, onApply, onView }) {
  const mr = s.meetsRequirements;
  const expired = isExpired(s.deadline);
  return (
    <div className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => onView(s)}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status]}`}>{s.status}</span>
            <span className="text-xs text-gray-500 dark:text-white/40">{s.scholarshipType}</span>
            {s.isVerified && <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 rounded-full">✓ Verified</span>}
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{s.title}</h3>
          <p className="text-sm text-gray-500 dark:text-white/40 mt-0.5">by {s.offeredBy?.name || s.offererName || 'Anonymous'}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-brand-600 dark:text-brand-400">₹{fmt(s.amount)}</div>
          <div className="text-xs text-gray-400">{s.disbursementType.replace('_', ' ')}</div>
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-white/60 line-clamp-2 mb-3">{s.description}</p>

      {/* Eligibility quick-check */}
      {mr && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {s.minCgpa > 0 && <EligBadge met={mr.cgpa} label={`CGPA ≥ ${s.minCgpa}`} />}
          {s.minAttendance > 0 && <EligBadge met={mr.attendance} label={`Attend. ≥ ${s.minAttendance}%`} />}
          {s.maxBacklogs < 999 && <EligBadge met={mr.backlogs} label={`Backlogs ≤ ${s.maxBacklogs}`} />}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-white/40">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><Users size={12} />{s.seatsFilled}/{s.seats} seats</span>
          <span className="flex items-center gap-1"><Users size={12} />{s.applicationCount} applied</span>
        </div>
        <span className={`flex items-center gap-1 ${expired ? 'text-red-500' : ''}`}>
          <Calendar size={12} />Deadline: {fmtDate(s.deadline)}
        </span>
      </div>

      {!s.hasApplied && s.status === 'OPEN' && !expired && mr && (
        <button
          className={`mt-3 w-full py-2 rounded-lg text-sm font-medium transition-colors ${mr.overall ? 'btn-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-white/40 cursor-not-allowed'}`}
          onClick={e => { e.stopPropagation(); if (mr.overall) onApply(s); }}
          disabled={!mr.overall}
        >
          {mr.overall ? 'Apply Now' : 'Not Eligible'}
        </button>
      )}
      {s.hasApplied && (
        <div className="mt-3 text-center text-sm text-green-600 dark:text-green-400 font-medium flex items-center justify-center gap-1">
          <CheckCircle size={14} /> Applied
        </div>
      )}
    </div>
  );
}

// ── ApplyModal ────────────────────────────────────────────────────────────────
function ApplyModal({ scholarship, onClose, onDone }) {
  const [essay, setEssay] = useState('');
  const [docs, setDocs] = useState([]);
  const [applyMut, { loading }] = useMutation(APPLY_FOR_SCHOLARSHIP, {
    refetchQueries: [{ query: GET_SCHOLARSHIPS }, { query: MY_SCHOLARSHIP_APPLICATIONS }],
  });

  const submit = async () => {
    await applyMut({ variables: { scholarshipId: scholarship.id, essay: essay || undefined, documents: docs } });
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
          <h2 className="font-semibold text-lg">Apply for {scholarship.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-brand-50 dark:bg-brand-900/20 rounded-xl p-3 text-sm">
            <div className="font-medium text-brand-700 dark:text-brand-300 mb-1">Scholarship Amount</div>
            <div className="text-2xl font-bold text-brand-600 dark:text-brand-400">₹{fmt(scholarship.amount)}</div>
          </div>

          {scholarship.requiredDocuments?.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">Required Documents</label>
              <div className="space-y-1">
                {scholarship.requiredDocuments.map(doc => (
                  <label key={doc} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="rounded"
                      checked={docs.includes(doc)}
                      onChange={e => setDocs(e.target.checked ? [...docs, doc] : docs.filter(d => d !== doc))} />
                    {doc}
                  </label>
                ))}
              </div>
            </div>
          )}

          {scholarship.essayRequired && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Essay {scholarship.essayPrompt && <span className="text-gray-400 font-normal">— {scholarship.essayPrompt}</span>}
              </label>
              <textarea className="input h-36 resize-none" placeholder="Write your essay here..."
                value={essay} onChange={e => setEssay(e.target.value)} />
              <div className="text-xs text-gray-400 text-right mt-0.5">{essay.length} chars</div>
            </div>
          )}
        </div>
        <div className="flex gap-3 p-5 border-t dark:border-gray-700">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          <button className="btn-primary flex-1" onClick={submit} disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DetailModal ───────────────────────────────────────────────────────────────
function DetailModal({ scholarship: s, onClose, onApply }) {
  if (!s) return null;
  const mr = s.meetsRequirements;
  const expired = isExpired(s.deadline);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h2 className="font-semibold text-lg">{s.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Amount + meta */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-3xl font-bold text-brand-600 dark:text-brand-400">₹{fmt(s.amount)} <span className="text-sm font-normal text-gray-400">{s.currency}</span></div>
              <div className="text-sm text-gray-500 mt-0.5">{s.disbursementType.replace('_', ' ')} · {s.scholarshipType}</div>
            </div>
            <div className="text-right text-sm">
              <div className="font-medium">{s.seatsFilled} / {s.seats} seats filled</div>
              <div className={`flex items-center gap-1 justify-end mt-0.5 ${expired ? 'text-red-500' : 'text-gray-500'}`}>
                <Calendar size={12} /> {fmtDate(s.deadline)}
              </div>
            </div>
          </div>

          <p className="text-gray-700 dark:text-white/70 text-sm leading-relaxed">{s.description}</p>

          {/* Eligibility */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> Eligibility Requirements</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {s.minCgpa > 0 && <div className="flex items-center justify-between"><span className="text-gray-500">Min CGPA</span><span className={`font-medium ${mr ? (mr.cgpa ? 'text-green-600' : 'text-red-500') : ''}`}>{s.minCgpa}</span></div>}
              {s.minAttendance > 0 && <div className="flex items-center justify-between"><span className="text-gray-500">Min Attendance</span><span className={`font-medium ${mr ? (mr.attendance ? 'text-green-600' : 'text-red-500') : ''}`}>{s.minAttendance}%</span></div>}
              {s.maxBacklogs < 999 && <div className="flex items-center justify-between"><span className="text-gray-500">Max Backlogs</span><span className={`font-medium ${mr ? (mr.backlogs ? 'text-green-600' : 'text-red-500') : ''}`}>{s.maxBacklogs}</span></div>}
              {s.genderRequirement !== 'ANY' && <div className="flex items-center justify-between"><span className="text-gray-500">Gender</span><span className="font-medium">{s.genderRequirement}</span></div>}
              {s.incomeLimit && <div className="flex items-center justify-between"><span className="text-gray-500">Income Limit</span><span className="font-medium">₹{fmt(s.incomeLimit)}</span></div>}
            </div>
            {s.eligibleYears?.length > 0 && <div className="mt-2 text-sm"><span className="text-gray-500">Years: </span>{s.eligibleYears.join(', ')}</div>}
            {s.eligibleBranches?.length > 0 && <div className="mt-1 text-sm"><span className="text-gray-500">Branches: </span>{s.eligibleBranches.join(', ')}</div>}
          </div>

          {/* Skills / Docs */}
          {s.requiredSkills?.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Required Skills</h3>
              <div className="flex flex-wrap gap-2">{s.requiredSkills.map(sk => <span key={sk} className="badge-gray">{sk}</span>)}</div>
            </div>
          )}
          {s.requiredDocuments?.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Required Documents</h3>
              <ul className="text-sm space-y-1">{s.requiredDocuments.map(d => <li key={d} className="flex items-center gap-2"><Check size={13} className="text-green-500" />{d}</li>)}</ul>
            </div>
          )}
          {s.essayRequired && <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm"><span className="font-medium">Essay Required</span>{s.essayPrompt && ` — ${s.essayPrompt}`}</div>}

          {/* Contact */}
          {(s.websiteUrl || s.contactEmail) && (
            <div className="flex gap-3 text-sm">
              {s.websiteUrl && <a href={s.websiteUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand-600 hover:underline"><ExternalLink size={13} /> Website</a>}
              {s.contactEmail && <a href={`mailto:${s.contactEmail}`} className="text-brand-600 hover:underline">{s.contactEmail}</a>}
            </div>
          )}

          {/* CTA */}
          {!s.hasApplied && s.status === 'OPEN' && !expired && (
            <button
              className={`w-full py-3 rounded-xl text-sm font-semibold ${mr?.overall ? 'btn-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'}`}
              disabled={!mr?.overall}
              onClick={() => { onClose(); onApply(s); }}
            >
              {mr?.overall ? 'Apply Now' : 'You Don\'t Meet Eligibility Requirements'}
            </button>
          )}
          {s.hasApplied && <div className="text-center py-3 text-green-600 dark:text-green-400 font-medium flex items-center justify-center gap-2"><CheckCircle size={16} /> Application Submitted</div>}
        </div>
      </div>
    </div>
  );
}

// ── OfferForm ─────────────────────────────────────────────────────────────────
function OfferForm({ onClose, onDone }) {
  const [form, setForm] = useState({
    title: '', description: '', amount: '', currency: 'INR', scholarshipType: 'MERIT',
    seats: 1, deadline: '', minCgpa: '', minAttendance: '', maxBacklogs: '',
    genderRequirement: 'ANY', incomeLimit: '',
    eligibleYears: '', eligibleBranches: '', eligibleColleges: '',
    requiredSkills: '', requiredDocuments: '',
    essayRequired: false, essayPrompt: '',
    disbursementType: 'ONE_TIME', selectionCriteria: '',
    websiteUrl: '', contactEmail: '', college: '',
    offererName: '', offererEmail: '',
  });
  const [createMut, { loading }] = useMutation(CREATE_SCHOLARSHIP, {
    refetchQueries: [{ query: GET_SCHOLARSHIPS }, { query: MY_SCHOLARSHIPS }],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const listOf = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
  const listOfInt = (s) => s ? s.split(',').map(x => parseInt(x.trim())).filter(n => !isNaN(n)) : [];

  const submit = async (e) => {
    e.preventDefault();
    await createMut({
      variables: {
        ...form,
        amount: parseFloat(form.amount),
        seats: parseInt(form.seats),
        minCgpa: form.minCgpa ? parseFloat(form.minCgpa) : undefined,
        minAttendance: form.minAttendance ? parseFloat(form.minAttendance) : undefined,
        maxBacklogs: form.maxBacklogs !== '' ? parseInt(form.maxBacklogs) : undefined,
        incomeLimit: form.incomeLimit ? parseFloat(form.incomeLimit) : undefined,
        eligibleYears: listOfInt(form.eligibleYears),
        eligibleBranches: listOf(form.eligibleBranches),
        eligibleColleges: listOf(form.eligibleColleges),
        requiredSkills: listOf(form.requiredSkills),
        requiredDocuments: listOf(form.requiredDocuments),
      },
    });
    onDone();
  };

  const F = ({ label, children }) => (
    <div><label className="block text-sm font-medium mb-1">{label}</label>{children}</div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h2 className="font-semibold text-lg">Offer a Scholarship</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <F label="Title *"><input required className="input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Merit Excellence Award" /></F>
            <F label="Amount (₹) *"><input required type="number" className="input" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="50000" /></F>
          </div>
          <F label="Description *"><textarea required className="input h-24 resize-none" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the scholarship, its purpose, and benefits..." /></F>
          <div className="grid grid-cols-3 gap-4">
            <F label="Type">
              <select className="input" value={form.scholarshipType} onChange={e => set('scholarshipType', e.target.value)}>
                {['MERIT','NEED_BASED','RESEARCH','SPORTS','CULTURAL','DIVERSITY','FIRST_GEN'].map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
            <F label="Seats"><input type="number" className="input" value={form.seats} onChange={e => set('seats', e.target.value)} min="1" /></F>
            <F label="Deadline *"><input required type="date" className="input" value={form.deadline} onChange={e => set('deadline', e.target.value)} /></F>
          </div>

          <div className="border dark:border-gray-700 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-sm text-gray-700 dark:text-white/60 uppercase tracking-wide">Eligibility Requirements</h3>
            <div className="grid grid-cols-3 gap-3">
              <F label="Min CGPA"><input type="number" step="0.01" className="input" value={form.minCgpa} onChange={e => set('minCgpa', e.target.value)} placeholder="0 = any" /></F>
              <F label="Min Attendance %"><input type="number" className="input" value={form.minAttendance} onChange={e => set('minAttendance', e.target.value)} placeholder="0 = any" /></F>
              <F label="Max Backlogs"><input type="number" className="input" value={form.maxBacklogs} onChange={e => set('maxBacklogs', e.target.value)} placeholder="999 = any" /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Eligible Years (comma-sep, e.g. 1,2,3)"><input className="input" value={form.eligibleYears} onChange={e => set('eligibleYears', e.target.value)} placeholder="leave blank = all" /></F>
              <F label="Eligible Branches"><input className="input" value={form.eligibleBranches} onChange={e => set('eligibleBranches', e.target.value)} placeholder="CSE, ECE, ME (blank = all)" /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Gender Requirement">
                <select className="input" value={form.genderRequirement} onChange={e => set('genderRequirement', e.target.value)}>
                  {['ANY','MALE','FEMALE','OTHER'].map(g => <option key={g}>{g}</option>)}
                </select>
              </F>
              <F label="Income Limit (₹/year)"><input type="number" className="input" value={form.incomeLimit} onChange={e => set('incomeLimit', e.target.value)} placeholder="blank = no limit" /></F>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <F label="Required Documents (comma-sep)"><input className="input" value={form.requiredDocuments} onChange={e => set('requiredDocuments', e.target.value)} placeholder="Marksheet, Income Cert, Photo ID" /></F>
            <F label="Required Skills (comma-sep)"><input className="input" value={form.requiredSkills} onChange={e => set('requiredSkills', e.target.value)} placeholder="Python, Research, Sports" /></F>
          </div>

          <div className="flex items-center gap-3">
            <input type="checkbox" id="essayReq" checked={form.essayRequired} onChange={e => set('essayRequired', e.target.checked)} className="rounded" />
            <label htmlFor="essayReq" className="text-sm">Require essay / statement of purpose</label>
          </div>
          {form.essayRequired && (
            <F label="Essay Prompt"><input className="input" value={form.essayPrompt} onChange={e => set('essayPrompt', e.target.value)} placeholder="Why do you deserve this scholarship?" /></F>
          )}

          <div className="border-t dark:border-gray-700 pt-4 grid grid-cols-2 gap-3">
            <F label="Your Name"><input className="input" value={form.offererName} onChange={e => set('offererName', e.target.value)} placeholder="Optional display name" /></F>
            <F label="Contact Email"><input type="email" className="input" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} /></F>
            <F label="Website URL"><input type="url" className="input" value={form.websiteUrl} onChange={e => set('websiteUrl', e.target.value)} placeholder="https://" /></F>
            <F label="Disbursement">
              <select className="input" value={form.disbursementType} onChange={e => set('disbursementType', e.target.value)}>
                {['ONE_TIME','MONTHLY','SEMESTER','ANNUAL'].map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>{loading ? 'Posting...' : 'Post Scholarship'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── MyOfferedTab ──────────────────────────────────────────────────────────────
function MyOfferedTab() {
  const { data, loading } = useQuery(MY_SCHOLARSHIPS);
  const [selected, setSelected] = useState(null);
  const [updateMut] = useMutation(UPDATE_SCHOLARSHIP, { refetchQueries: [{ query: MY_SCHOLARSHIPS }] });
  const [deleteMut] = useMutation(DELETE_SCHOLARSHIP, { refetchQueries: [{ query: MY_SCHOLARSHIPS }, { query: GET_SCHOLARSHIPS }] });
  const { data: appData } = useQuery(SCHOLARSHIP_APPLICANTS, { variables: { scholarshipId: selected }, skip: !selected });
  const [reviewMut] = useMutation(REVIEW_SCHOLARSHIP_APPLICATION, { refetchQueries: [{ query: SCHOLARSHIP_APPLICANTS, variables: { scholarshipId: selected } }] });

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  const scholarships = data?.myScholarships || [];

  return (
    <div className="space-y-4">
      {scholarships.length === 0 && <div className="text-center py-12 text-gray-400">You haven't offered any scholarships yet.</div>}
      {scholarships.map(s => (
        <div key={s.id} className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{s.title}</h3>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status]}`}>{s.status}</span>
              <button onClick={() => updateMut({ variables: { scholarshipId: s.id, status: s.status === 'OPEN' ? 'CLOSED' : 'OPEN' } })}
                className="btn-secondary text-xs py-1 px-2">{s.status === 'OPEN' ? 'Close' : 'Reopen'}</button>
              <button onClick={() => { if (window.confirm('Delete this scholarship?')) deleteMut({ variables: { scholarshipId: s.id } }); }}
                className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-white/40 mb-3">
            ₹{fmt(s.amount)} · {s.applicationCount} applications · {s.seatsFilled}/{s.seats} seats filled · Deadline {fmtDate(s.deadline)}
          </div>
          <button onClick={() => setSelected(selected === s.id ? null : s.id)}
            className="text-sm text-brand-600 dark:text-brand-400 flex items-center gap-1">
            {selected === s.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {selected === s.id ? 'Hide' : 'View'} Applicants
          </button>
          {selected === s.id && (
            <div className="mt-3 space-y-3 border-t dark:border-gray-700 pt-3">
              {(appData?.scholarshipApplicants || []).length === 0 && <p className="text-sm text-gray-400">No applications yet.</p>}
              {(appData?.scholarshipApplicants || []).map(app => (
                <div key={app.id} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{app.applicant.name}</div>
                    <div className="text-xs text-gray-500">{app.applicant.college} · {app.applicant.branch} Yr{app.applicant.year}</div>
                    {app.cgpaSnapshot && <div className="text-xs text-gray-400">CGPA: {app.cgpaSnapshot}</div>}
                    {app.essay && <p className="text-xs mt-1 text-gray-600 dark:text-white/60 line-clamp-2">{app.essay}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${APP_STATUS_COLORS[app.status]}`}>{app.status}</span>
                    {app.status === 'PENDING' && (
                      <>
                        <button onClick={() => reviewMut({ variables: { applicationId: app.id, status: 'ACCEPTED' } })}
                          className="p-1 text-green-600 hover:bg-green-100 rounded-lg"><Check size={14} /></button>
                        <button onClick={() => reviewMut({ variables: { applicationId: app.id, status: 'REJECTED' } })}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-lg"><X size={14} /></button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── MyApplicationsTab ─────────────────────────────────────────────────────────
function MyApplicationsTab() {
  const { data, loading } = useQuery(MY_SCHOLARSHIP_APPLICATIONS);
  const [withdrawMut] = useMutation(WITHDRAW_SCHOLARSHIP_APPLICATION, {
    refetchQueries: [{ query: MY_SCHOLARSHIP_APPLICATIONS }, { query: GET_SCHOLARSHIPS }],
  });

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  const apps = data?.myScholarshipApplications || [];

  return (
    <div className="space-y-4">
      {apps.length === 0 && <div className="text-center py-12 text-gray-400">You haven't applied to any scholarships yet.</div>}
      {apps.map(app => (
        <div key={app.id} className="card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{app.scholarship.title}</h3>
              <div className="text-lg font-bold text-brand-600 dark:text-brand-400">₹{fmt(app.scholarship.amount)}</div>
              <div className="text-xs text-gray-400 mt-1">Applied {fmtDate(app.appliedAt)}</div>
              {app.adminNote && <div className="text-sm mt-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">{app.adminNote}</div>}
            </div>
            <div className="text-right shrink-0">
              <span className={`text-xs px-2 py-1 rounded-full ${APP_STATUS_COLORS[app.status]}`}>{app.status}</span>
              {app.status === 'PENDING' && (
                <button onClick={() => { if (window.confirm('Withdraw application?')) withdrawMut({ variables: { applicationId: app.id } }); }}
                  className="block mt-2 text-xs text-red-500 hover:underline ml-auto">Withdraw</button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ScholarshipPage() {
  const [tab, setTab] = useState('browse');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ type: '', minAmount: '', maxAmount: '' });
  const [applying, setApplying] = useState(null);   // scholarship object
  const [viewing, setViewing] = useState(null);     // scholarship object
  const [showOffer, setShowOffer] = useState(false);

  const { data, loading, refetch } = useQuery(GET_SCHOLARSHIPS, {
    variables: {
      search: search || undefined,
      type: filters.type || undefined,
      minAmount: filters.minAmount ? parseFloat(filters.minAmount) : undefined,
      maxAmount: filters.maxAmount ? parseFloat(filters.maxAmount) : undefined,
    },
  });

  const scholarships = data?.scholarships || [];

  const TABS = [
    { id: 'browse',  label: 'Browse',         icon: Search },
    { id: 'applied', label: 'My Applications', icon: BookOpen },
    { id: 'offered', label: 'My Offered',      icon: Award },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Award className="text-brand-600 dark:text-brand-400" /> Scholarships</h1>
          <p className="text-sm text-gray-500 dark:text-white/40 mt-0.5">Discover funding opportunities and offer scholarships to deserving students</p>
        </div>
        <button onClick={() => setShowOffer(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Offer Scholarship</button>
      </div>

      {/* Tabs */}
      <div className="flex border-b dark:border-gray-700 gap-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-brand-600 text-brand-600 dark:text-brand-400 dark:border-brand-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-white/40 dark:hover:text-white/60'}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'applied' && <MyApplicationsTab />}
      {tab === 'offered' && <MyOfferedTab />}

      {tab === 'browse' && (
        <>
          {/* Search + Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input pl-9" placeholder="Search scholarships..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="input w-40" value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
              <option value="">All Types</option>
              {['MERIT','NEED_BASED','RESEARCH','SPORTS','CULTURAL','DIVERSITY','FIRST_GEN'].map(t => <option key={t}>{t}</option>)}
            </select>
            <input className="input w-32" type="number" placeholder="Min ₹" value={filters.minAmount} onChange={e => setFilters(f => ({ ...f, minAmount: e.target.value }))} />
            <input className="input w-32" type="number" placeholder="Max ₹" value={filters.maxAmount} onChange={e => setFilters(f => ({ ...f, maxAmount: e.target.value }))} />
          </div>

          {/* Stats bar */}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-white/40">
            <span>{scholarships.length} scholarships found</span>
            <span>·</span>
            <span>{scholarships.filter(s => s.meetsRequirements?.overall).length} you're eligible for</span>
          </div>

          {loading && <div className="text-center py-12 text-gray-400">Loading scholarships...</div>}
          {!loading && scholarships.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Award size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No scholarships found</p>
              <p className="text-sm mt-1">Be the first to offer one.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scholarships.map(s => (
              <ScholarshipCard key={s.id} s={s} onApply={setApplying} onView={setViewing} />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {showOffer && <OfferForm onClose={() => setShowOffer(false)} onDone={() => { setShowOffer(false); refetch(); }} />}
      {applying && <ApplyModal scholarship={applying} onClose={() => setApplying(null)} onDone={() => { setApplying(null); refetch(); }} />}
      {viewing && <DetailModal scholarship={viewing} onClose={() => setViewing(null)} onApply={(s) => { setViewing(null); setApplying(s); }} />}
    </div>
  );
}
